import type { Lead } from './types'
import { el } from './ui'
import { roundConfig, screenLocale } from './round-config'

export type { Step } from './step-types'
import type { Step } from './step-types'
export interface StepBarOptions {
  label?: string
  // Hosts can read existing evidence on demand without changing the steps' recorded states.
  describe?: (step: Step) => Promise<Partial<Pick<Step, 'when' | 'detail' | 'goBack'>>>
  // A refused or withdrawn journey has no pending action and keeps its missing evidence grey.
  ended?: boolean
  justCompleted?: boolean
}
let boxId = 0
const completedEnquiries = new Map<string, boolean>()

// Unknown is evidence missing, not a pending action. It must never be normalised to done or next.
export function stepBar(input: readonly Step[], opts: StepBarOptions = {}): HTMLElement {
  if (input.length < 2 || input.length > 6) throw new RangeError('A step bar needs 2 to 6 steps')
  if (new Set(input.map(s => s.key)).size !== input.length) throw new Error('Step keys must be unique')
  const steps = input.map(s => ({ ...s }))
  let next = steps.findIndex(s => s.state === 'next')
  if (!opts.ended && next < 0 && steps.at(-1)?.state !== 'done') next = steps.findIndex(s => s.state === 'later')
  if (!opts.ended && next < 0 && steps.at(-1)?.state !== 'done') throw new Error('An unfinished step bar needs a next step')
  steps.forEach((s, i) => { if (i === next) s.state = 'next'; else if (s.state === 'next') s.state = 'later' })
  const complete = steps.every(s => s.state === 'done')
  const bar = el('div', { class: `enquiry-steps${complete ? ' enquiry-steps-complete' : ''}${complete && opts.justCompleted ? ' enquiry-steps-just-completed' : ''}`, role: 'group', 'aria-label': opts.label ?? 'Steps' })
  bar.setAttribute('style', `--step-count: ${steps.length}`)
  const box = el('div', { class: 'enquiry-step-box', role: 'region', 'aria-label': 'Step details', 'aria-live': 'polite', id: `step-box-${++boxId}` })
  box.hidden = true
  const buttons: HTMLButtonElement[] = []
  let opened: HTMLButtonElement | null = null
  let request = 0
  const close = (): void => {
    request++; box.hidden = true
    opened?.setAttribute('aria-expanded', 'false')
    opened?.focus(); opened = null
  }
  // Clicks still reach the page, so an open menu closes on a tap here (outside taps close menus); the row's own opener
  // is a sibling button and the host opens it only for taps on itself. A swipe never starts from a step.
  bar.addEventListener('pointerdown', e => e.stopPropagation())
  bar.addEventListener('keydown', e => { if (e.key === 'Escape' && !box.hidden) { e.stopPropagation(); close() } })
  const latest = steps.map(s => s.state === 'done').lastIndexOf(true)
  steps.forEach((step, i) => {
    const said = step.state === 'unknown' ? 'not recorded' : step.state === 'next' ? `next, ${step.turn === 'them' ? 'waiting on them' : 'your turn'}` : step.state
    const button = el('button', { type: 'button', class: `enquiry-step enquiry-step-${step.state}`, 'data-step': step.key, 'data-state': step.state, ...(step.state === 'next' ? { 'data-turn': step.turn ?? 'you' } : {}), 'aria-label': `${step.word}: ${said}${step.when ? `, ${step.when}` : ''}`, 'aria-expanded': 'false', 'aria-controls': box.id }, el('span', { class: 'enquiry-step-mark', 'aria-hidden': 'true' }), step.state === 'skipped' ? 'skipped' : step.word)
    if (complete && i === steps.length - 1) {
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      tick.setAttribute('viewBox', '0 0 24 24'); tick.setAttribute('class', 'enquiry-steps-tick'); tick.setAttribute('aria-hidden', 'true')
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute('d', 'M5 12l4 4L19 6'); tick.append(path); button.append(tick)
    }
    buttons.push(button)
    button.addEventListener('click', async () => {
      if (opened === button) { close(); return }
      buttons.forEach(b => b.setAttribute('aria-expanded', 'false'))
      opened = button; button.setAttribute('aria-expanded', 'true'); box.hidden = false
      const ticket = ++request
      const show = (extra: Partial<Step> = {}, loading = false): void => {
        const s = { ...step, ...extra }
        if (s.when) button.setAttribute('aria-label', `${step.word}: ${said}, ${s.when}`)
        box.replaceChildren(el('strong', {}, `${s.word}${s.when ? ` ${s.when}` : ''}`), el('span', {}, s.state === 'done' ? s.detail ?? (s.when ? `Done ${s.when}` : loading ? 'Checking when it happened…' : 'Done. Date not recorded.') : s.state === 'skipped' ? s.detail ?? 'This step was skipped.' : s.waitingFor ?? (s.state === 'unknown' ? 'Not recorded.' : s.turn === 'them' ? 'Waiting for their reply' : `Waiting for you: ${s.word.toLowerCase()}`)))
        if (s.state === 'done' && i === latest && s.goBack) {
          const back = el('button', { type: 'button', class: 'enquiry-step-back' }, s.goBackLabel ?? "Didn't send it? Go back a step")
          back.addEventListener('click', async () => {
            back.disabled = true
            try {
              const goneBack = await s.goBack!()
              if (request !== ticket) return
              if (goneBack) close(); else back.disabled = false
            } catch {
              if (request === ticket) { back.disabled = false; box.append(el('span', { role: 'status' }, "Couldn't go back. Try again.")) }
            }
          })
          box.append(back)
        }
        const hide = el('button', { type: 'button', class: 'enquiry-step-close' }, 'Close')
        hide.addEventListener('click', close); box.append(hide)
      }
      show({}, !!opts.describe && step.state === 'done')
      if (opts.describe && step.state === 'done') {
        try { const extra = await opts.describe(step); if (request === ticket) show(extra) }
        catch {
          if (request === ticket) show({ detail: "Done. Couldn't check the date. Close and try again." })
        }
      }
    })
    bar.append(button)
  })
  bar.append(box)
  return bar
}

// Evidence comes from recorded actions, never a price, draft, or inferred answer.
export function enquirySteps(lead: Lead, turn: 'you' | 'them' = 'you', opts: StepBarOptions = {}): HTMLElement {
  const answered = !!lead.progress_answered || !lead.progress_quoted && (lead.enquiry_messages ?? 0) > 0
  const facts = [true, answered, !!lead.progress_quoted, !!lead.progress_yes, !!lead.progress_booked]
  const reached = facts.lastIndexOf(true)
  const identity = `${lead.business_id ?? 'unknown'}:${lead.id}`
  const complete = facts.every(Boolean)
  const justCompleted = completedEnquiries.get(identity) === false && complete
  completedEnquiries.set(identity, complete)
  // The first step is "Enquiry", not "Asked" (Liam, 2 Nov 2026: "it seems like i asked but actually its they asked"); its
  // key stays 'asked' so saved marks and checks still find it.
  return stepBar(['Enquiry', 'Answered', 'Quoted', 'Yes', 'Booked'].map((word, i): Step => ({
    key: i === 0 ? 'asked' : word.toLowerCase(), word, turn,
    state: facts[i] ? 'done' : i === reached + 1 ? 'next' : i < reached && lead.progress_known ? 'skipped' : 'unknown',
    ...(i === 0 && lead.progress_known && lead.created_at ? { when: stepDate(lead.created_at), detail: `They got in touch ${stepDate(lead.created_at)}` } : {}),
    // Only steps still ahead wait for something; a grey gap before the furthest green step is just not recorded.
    ...(i === reached + 1 ? { waitingFor: turn === 'them' ? 'Waiting for their reply' : `Waiting for ${['their enquiry', 'your answer', 'your quote', 'their agreement', 'a booking'][i]}` }
      : i > reached ? { waitingFor: `Waiting for ${['their enquiry', 'your answer', 'your quote', 'their agreement', 'a booking'][i]}` } : {}),
  })), { label: 'Enquiry steps', justCompleted, ...opts })
}

export function stepDate(value: string): string {
  const date = new Date(/^\d{4}-\d{2}-\d{2} /.test(value) ? `${value.replace(' ', 'T')}Z` : value)
  if (Number.isNaN(date.getTime())) return 'date not recorded'
  return new Intl.DateTimeFormat(screenLocale(), { timeZone: value.length > 10 ? roundConfig().timeZone : 'UTC', day: 'numeric', month: 'short', ...(value.length > 10 ? { hour: '2-digit', minute: '2-digit' } : {}) }).format(date)
}
